import {ChangeDetectionStrategy, Component, inject} from '@angular/core';
import {MatCard, MatCardContent, MatCardTitle} from '@angular/material/card';
import {ExtendedFormGroup} from '@aviary/forms/extensions/extended-form-group/extended-form-group';
import {buildFormGroup, getExtendedControlFrom} from '@aviary/forms/form.helpers';
import {PatronSelectors} from '@store/patron/patron.selectors';
import {PatronValidators} from '@store/patron/patron.validators';
import {Store} from '@ngrx/store';
import {SelectPipe} from '@aviary/pipes/select.pipe';
import {HiddenUntilLoadedDirective} from '@aviary/directives/hidden-until-loaded.directive';
import {InputFormFieldComponent} from '@aviary/forms/fields/input-form-field/input-form-field.component';
import {PatronActions} from '@store/patron/patron.actions';
import {FormRowComponent} from '@aviary/forms/fields/form-row/form-row.component';
import {NgTemplateOutlet} from '@angular/common';
import {SelectFormFieldComponent} from '@aviary/forms/fields/select-form-field/select-form-field.component';
import {
  EXTENDED_PATRON_NOTIFICATION_PREFERENCE_SELECT_OPTIONS,
  PatronSettingsQuickTogglesValue,
  STANDARD_PATRON_NOTIFICATION_PREFERENCE_SELECT_OPTIONS
} from '@store/patron/patron.types';
import {isPopulatedString} from '@aviary/store/common/typing.helpers';
import {ExtendedFormControl} from '@aviary/forms/extensions/extended-form-control/extended-form-control';
import {snapshot} from '@aviary/store/store.helpers';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {filter, skip, take} from 'rxjs/operators';
import {FALLBACK_NOTIFICATION_PREFERENCES_EDIT_VALUE, FALLBACK_PATRON_SETTINGS_QUICK_TOGGLE_VALUE} from '@store/patron/patron.fallback';
import {concatLatestFrom} from '@ngrx/operators';
import {MatTooltip} from '@angular/material/tooltip';
import {SlideToggleFormFieldComponent} from '@aviary/forms/fields/slide-toggle-form-field/slide-toggle-form-field.component';
import {debounceTime} from 'rxjs';
import {ValidatorFn, Validators} from '@angular/forms';
import {CommonValidators} from '@aviary/store/common/common.validators';
import {SmsTermsCheckboxComponent} from '@lib/components/sms-terms-checkbox/sms-terms-checkbox.component';
import {FilledButtonComponent} from '@aviary/buttons/filled-button/filled-button.component';
import {MatDivider} from '@angular/material/list';

@Component({
  selector: 'px-settings',
  templateUrl: './settings.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatCard, MatCardTitle, MatCardContent, SelectPipe, HiddenUntilLoadedDirective, InputFormFieldComponent, FormRowComponent, NgTemplateOutlet,
    SelectFormFieldComponent, MatTooltip, SlideToggleFormFieldComponent, SmsTermsCheckboxComponent, FilledButtonComponent, MatDivider
  ]
})
export class SettingsComponent {
  private readonly store: Store = inject(Store);
  public readonly preferencesSelectors = PatronSelectors.NotificationPreferences;
  public readonly currentPatronSelectors = PatronSelectors.Auth;
  public readonly formGroup: ExtendedFormGroup = buildFormGroup(
    FALLBACK_NOTIFICATION_PREFERENCES_EDIT_VALUE,
    inject(PatronValidators).editNotificationPreferences,
  );
  public readonly quickTogglesFormGroup: ExtendedFormGroup = buildFormGroup(
    FALLBACK_PATRON_SETTINGS_QUICK_TOGGLE_VALUE, {}
  );

  private readonly smsControl: ExtendedFormControl = getExtendedControlFrom(this.formGroup, 'phoneSms');
  private readonly emailControl: ExtendedFormControl = getExtendedControlFrom(this.formGroup, 'notificationEmail');
  private readonly termsAcceptedControl: ExtendedFormControl = getExtendedControlFrom(this.formGroup, 'smsTermsAccepted');
  public readonly phoneOptionEnabled: boolean = isPopulatedString(snapshot(this.currentPatronSelectors.phoneCall));
  public smsNumberSelected: boolean = false;
  public isTermsAcceptedValid: boolean = false;
  private initialized: boolean = false;
  private readonly notificationPreferenceFieldNames: string[] = [
    'accountNotifications', 'renewNotifications', 'expirationNotifications', 'receiptNotifications',
    'holdNotifications', 'noticeNotifications', 'itemRequestNotifications'
  ];

  constructor() {
    this.setValidators();
    this.store.select(this.preferencesSelectors.finishedLoading)
      .pipe(
        filter(Boolean),
        concatLatestFrom(() => [
          this.store.select(this.preferencesSelectors.notificationPreferencesEditValue),
          this.store.select(this.currentPatronSelectors.settingsQuickTogglesEditValue)
        ]),
        take(1),
        takeUntilDestroyed()
      )
      .subscribe(([, notificationPreferencesEditValue, quickTogglesEditValue]) => {
        this.formGroup.patchValue(notificationPreferencesEditValue);
        this.quickTogglesFormGroup.patchValue(quickTogglesEditValue);
        this.smsNumberSelected = isPopulatedString(notificationPreferencesEditValue.phoneSms);
        this.isTermsAcceptedValid = notificationPreferencesEditValue.smsTermsAccepted || !this.smsNumberSelected;

        // Once, after the initial values have all been loaded, uncheck the terms accepted checkbox if it is not valid.
        if (!this.isTermsAcceptedValid) {
          this.deselectTermsAccepted();
        }
        this.initialized = true;
        this.rerunValidators();
      });

    this.smsControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(value => {
        this.smsNumberSelected = isPopulatedString(value);
        this.termsAcceptedControl.setValidators(this.smsNumberSelected ? [Validators.requiredTrue] : []);

        // If the form has already been initialized, and the control has been set to *any* value by the user, uncheck the 'Terms Accepted' checkbox.
        if (this.initialized && this.smsControl.dirty) {
          this.deselectTermsAccepted();
        }
        this.rerunValidators();
      });

    this.emailControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.rerunValidators();
      });

    // SMS Terms Accepted changes will change the state of the save button.
    this.termsAcceptedControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.rerunValidators();
        this.isTermsAcceptedValid = this.termsAcceptedControl?.valid;
      });

    // If the patron changes any quick toggle value, update the API.
    this.quickTogglesFormGroup.valueChanges
      .pipe(takeUntilDestroyed(), debounceTime(500), skip(1))
      .subscribe((value: PatronSettingsQuickTogglesValue) => {
        this.store.dispatch(PatronActions.updatePatronQuickToggles(value));
      });
  }

  private setValidators(): void {
    const validators: ValidatorFn[] = [
      CommonValidators.requiresSmsNumberValidator(this.smsControl), CommonValidators.requiresNotificationEmailValidator(this.emailControl),
      CommonValidators.smsTermsValidator(this.termsAcceptedControl)
    ];
    for (const field of this.notificationPreferenceFieldNames) {
      const control = getExtendedControlFrom(this.formGroup, field);
      control.setValidators(validators);
    }
  }

  // Forcibly re-run the validators on our notification preference fields, since they won't run automatically when other attached fields change.
  private rerunValidators(): void {
    if (this.initialized) {
      for (const field of this.notificationPreferenceFieldNames) {
        const control = getExtendedControlFrom(this.formGroup, field);
        control.markAsTouched();
        control.updateValueAndValidity();
      }
    }
  }

  private deselectTermsAccepted(): void {
    this.termsAcceptedControl.patchValue(false);
    this.termsAcceptedControl.updateValueAndValidity();
  }

  public onClickSaveButton(): void {
    if (this.formGroup.valid) {
      this.store.dispatch(PatronActions.editNotificationPreferences(this.formGroup.value));
    }
  }

  public readonly EXTENDED_PATRON_NOTIFICATION_PREFERENCE_SELECT_OPTIONS = EXTENDED_PATRON_NOTIFICATION_PREFERENCE_SELECT_OPTIONS;
  public readonly STANDARD_PATRON_NOTIFICATION_PREFERENCE_SELECT_OPTIONS = STANDARD_PATRON_NOTIFICATION_PREFERENCE_SELECT_OPTIONS;
}
